import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import PaymentModal from '../../components/ui/PaymentModal';
import MetricCard from './components/MetricCard';
import RevenueChart from './components/RevenueChart';
import AIInsightPanel from './components/AIInsightPanel';
import QuickActionTile from './components/QuickActionTile';

const Dashboard = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const metrics = [
    {
      title: 'Business Health Index',
      value: 87,
      maxValue: 100,
      trend: 'up',
      trendValue: 5.2,
      icon: 'Activity',
      description: 'Overall business performance score'
    },
    {
      title: 'Strategy Strength',
      value: 82,
      maxValue: 100,
      trend: 'up',
      trendValue: 3.8,
      icon: 'Target',
      description: 'Business model effectiveness rating'
    },
    {
      title: 'Financial Stability',
      value: 91,
      maxValue: 100,
      trend: 'up',
      trendValue: 2.4,
      icon: 'DollarSign',
      description: 'Revenue and cost health indicator'
    },
    {
      title: 'Goal Feasibility',
      value: 78,
      maxValue: 100,
      trend: 'neutral',
      trendValue: 0.5,
      icon: 'TrendingUp',
      description: 'Target achievement probability'
    }
  ];

  const quickActions = [
    {
      title: 'Business Model Canvas',
      description: 'Design and refine your business strategy with AI-powered suggestions for each component',
      icon: 'Grid3x3',
      path: '/business-model-canvas',
      color: 'primary'
    },
    {
      title: 'Predictive Analysis',
      description: 'Forecast revenue trends and identify growth opportunities with advanced analytics',
      icon: 'LineChart',
      path: '/predictive-analysis',
      color: 'secondary'
    },
    {
      title: 'Goal Intelligence',
      description: 'Set strategic goals and track feasibility with AI-driven milestone planning',
      icon: 'Target',
      path: '/goal-intelligence',
      color: 'accent'
    }
  ];

  const handleGenerateReport = () => {
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    console.log('Payment successful - generating report');
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={isSidebarCollapsed} 
        userSubscription="free"
      />
      <AuthHeader 
        isAuthenticated={true}
        userName="John Smith"
        userSubscription="free"
      />
      <main 
        className={`pt-20 pb-8 transition-smooth ${
          isSidebarCollapsed ? 'lg:pl-24' : 'lg:pl-64'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground font-heading mb-2">
              Dashboard Overview
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body">
              Monitor your business health and make data-driven decisions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {metrics?.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <RevenueChart />
            </div>
            <div className="lg:col-span-1">
              <AIInsightPanel onGenerateReport={handleGenerateReport} />
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground font-heading mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {quickActions?.map((action, index) => (
                <QuickActionTile key={index} {...action} />
              ))}
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 17L12 22L22 17" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-foreground font-heading mb-1">
                    Upgrade to Premium
                  </h3>
                  <p className="text-sm text-muted-foreground font-body">
                    Unlock advanced analytics, unlimited reports, and priority AI insights
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsPaymentModalOpen(true)}
                className="px-4 py-2 bg-accent text-accent-foreground rounded-md font-medium font-body text-sm hover:bg-accent/90 transition-smooth elevation-sm hover:elevation-md whitespace-nowrap"
              >
                View Plans
              </button>
            </div>
          </div>
        </div>
      </main>
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        featureName="Comprehensive Decision Report"
        featureDescription="Get detailed business analysis with AI-powered recommendations and actionable insights"
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default Dashboard;